// ── Shared helpers ─────────────────────────────────────────────────────────
export const API = process.env.REACT_APP_API_BASE_URL;

export const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n ?? 0);

// ── Status badge ──────────────────────────────────────────────────────────
const STATUS_MAP = {
  PAID:      { bg: "var(--success-bg)",  color: "var(--success-text)",  border: "var(--success-border)",  label: "Paid" },
  CREATED:   { bg: "var(--warning-bg)",  color: "var(--warning-text)",  border: "var(--warning-border)",  label: "Created" },
  PENDING:   { bg: "var(--warning-bg)",  color: "var(--warning-text)",  border: "var(--warning-border)",  label: "Pending" },
  CONFIRMED: { bg: "var(--success-bg)",  color: "var(--success-text)",  border: "var(--success-border)",  label: "Confirmed" },
  SHIPPED:   { bg: "#0a1a2e",            color: "#60a5fa",              border: "#1a3a5e",                label: "Shipped" },
  DELIVERED: { bg: "var(--success-bg)",  color: "var(--success-text)",  border: "var(--success-border)",  label: "Delivered" },
  CANCELLED: { bg: "var(--danger-bg)",   color: "var(--danger-text)",   border: "var(--danger-border)",   label: "Cancelled" },
  REFUNDED:  { bg: "#1a0d2e",            color: "#c084fc",              border: "#2e1a4e",                label: "Refunded" },
  COD:       { bg: "#1a1200",            color: "#f59e0b",              border: "#3d2e00",                label: "COD" },
};

export function StatusBadge({ value }) {
  const s = STATUS_MAP[value] || {
    bg: "var(--bg-secondary)", color: "var(--text-muted)",
    border: "var(--border-subtle)", label: value,
  };
  return (
    <span style={{
      background: s.bg, color: s.color,
      border: `1px solid ${s.border}`,
      padding: "2px 9px",
      borderRadius: "var(--radius-pill)",
      fontSize: 10, fontWeight: 500,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      display: "inline-block",
      fontFamily: "var(--font-mono)",
    }}>
      {s.label}
    </span>
  );
}

// ── Table header cell ──────────────────────────────────────────────────────
export function TH({ children }) {
  return (
    <th style={{
      padding: "11px 16px",
      textAlign: "left",
      color: "var(--text-muted)",
      fontSize: 9,
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.14em",
      borderBottom: "1px solid var(--border-subtle)",
      whiteSpace: "nowrap",
      fontFamily: "var(--font-mono)",
      background: "var(--bg-secondary)",
    }}>
      {children}
    </th>
  );
}

// ── Table data cell ────────────────────────────────────────────────────────
export function TD({ children, style }) {
  return (
    <td style={{
      padding: "12px 16px",
      borderBottom: "1px solid var(--border-subtle)",
      fontSize: 13,
      color: "var(--text-primary)",
      verticalAlign: "middle",
      ...style,
    }}>
      {children}
    </td>
  );
}

// ── Shared input style ─────────────────────────────────────────────────────
export const inputStyle = {
  background: "var(--bg-input)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-sm)",
  color: "var(--text-primary)",
  padding: "9px 12px",
  fontSize: 13,
  outline: "none",
  fontFamily: "var(--font-sans)",
  width: "100%",
  transition: "border-color var(--transition)",
};

// ── Page heading ───────────────────────────────────────────────────────────
export function PageHeading({ title, sub, action }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      flexWrap: "wrap",
      gap: 12,
      marginBottom: 28,
      paddingBottom: 20,
      borderBottom: "1px solid var(--border-subtle)",
    }}>
      <div>
        <h1 style={{
          fontFamily: "var(--font-display)",
          fontSize: "clamp(28px, 5vw, 40px)",
          letterSpacing: "0.05em",
          color: "var(--text-primary)",
          lineHeight: 1,
          marginBottom: 6,
        }}>
          {title.toUpperCase()}
        </h1>
        {sub && (
          <p style={{
            color: "var(--text-muted)",
            fontSize: 11,
            letterSpacing: "0.08em",
            fontFamily: "var(--font-mono)",
          }}>
            {sub}
          </p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── Card wrapper ───────────────────────────────────────────────────────────
export function AdminCard({ children, style }) {
  return (
    <div
      className="admin-card-wrap"
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        // overflow handled via .admin-card-wrap CSS class in AdminLayout
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────
export function Modal({ onClose, children, width = 480 }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 300,
        backdropFilter: "blur(3px)",
        padding: "16px",
        animation: "auModalIn 0.2s ease both",
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes auModalIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes auModalSlide {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-lg)",
          padding: "28px",
          width: "100%",
          maxWidth: width,
          maxHeight: "90vh",
          overflowY: "auto",
          animation: "auModalSlide 0.22s ease both",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ── Modal heading ──────────────────────────────────────────────────────────
export function ModalHeader({ title, onClose }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 22,
      paddingBottom: 16,
      borderBottom: "1px solid var(--border-subtle)",
    }}>
      <h2 style={{
        fontFamily: "var(--font-display)",
        fontSize: 22,
        letterSpacing: "0.06em",
        color: "var(--text-primary)",
      }}>
        {title.toUpperCase()}
      </h2>
      <button
        onClick={onClose}
        style={{
          background: "var(--bg-secondary)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-sm)",
          color: "var(--text-muted)",
          width: 30, height: 30,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", fontSize: 16, lineHeight: 1,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
